import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { BLOCKED_BROWSER_SHORTCUTS, matches } from '@/lib/browserShortcuts';

// Routes where losing in-progress work (an open cart / purchase draft) actually costs
// something — gates the beforeunload backstop so staff aren't nagged closing a tab
// while idly viewing Reports/Settings/Dashboard.
const TRANSACTIONAL_PATH_PREFIXES = ['/pos', '/purchases'];

// Single app-root-level guard: (a) suppresses browser-chrome shortcuts (history,
// bookmarks, save-page, etc.) so they never interrupt POS keyboard flows, and
// (b) shows a native "leave site?" confirmation as a backstop for the handful of
// shortcuts (Ctrl+W, Alt+F4) that browsers never let JS intercept at all.
export function useBrowserShortcutGuard() {
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  useEffect(() => { pathRef.current = location.pathname; });

  useEffect(() => {
    const handler = (e) => {
      for (const def of BLOCKED_BROWSER_SHORTCUTS) {
        if (matches(e, def)) {
          e.preventDefault();
          return;
        }
      }
    };
    // Capture phase, like useBarcodeScanner.js — runs before any descendant's
    // stopPropagation() can shadow it, so a Ctrl+S pressed inside a modal input
    // still gets blocked.
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const isTransactional = TRANSACTIONAL_PATH_PREFIXES.some(p => pathRef.current.startsWith(p));
      if (!isTransactional) return;
      e.preventDefault();
      e.returnValue = ''; // required for Chrome
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);
}
